import { useMemo } from "react";
import { compileExpr, FIGURE_COLORS, FIGURE_PRESETS, FigureSpec, readFigureSpec } from "@jange/shared";

type Spec = FigureSpec;
type Slider = Spec["sliders"][number];
type Curve = Spec["curves"][number];

const COLOR_LABEL: Record<string, string> = { teal: "Principale (vert)", amber: "Ambre", ink: "Encre", muted: "Gris (repère)" };
const num = (s: string, fallback: number) => { const v = Number(s); return s.trim() === "" || !Number.isFinite(v) ? fallback : v; };

/** Éditeur de figure : courbes y = f(x), curseurs, point mobile. La validation est la même que celle du serveur. */
export function FigureEditor({ value, lang, onChange }: { value: unknown; lang: string; onChange: (spec: Spec) => void }) {
  // `value` peut être momentanément invalide pendant la saisie : on l'édite tel quel.
  // Une spec absente (anciennes leçons) s'ouvre sur la parabole par défaut.
  const raw = useMemo<Spec>(() => (value && typeof value === "object" && (value as { kind?: string }).kind === "function-plot" ? value as Spec : readFigureSpec(value)), [value]);
  const check = useMemo(() => FigureSpec.safeParse(raw), [raw]);
  const names = raw.sliders.map((s) => s.name);
  const set = (patch: Partial<Spec>) => onChange({ ...raw, ...patch });
  const setSlider = (i: number, p: Partial<Slider>) => set({ sliders: raw.sliders.map((s, k) => (k === i ? { ...s, ...p } : s)) });
  const setCurve = (i: number, p: Partial<Curve>) => set({ curves: raw.curves.map((c, k) => (k === i ? { ...c, ...p } : c)) });

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row" style={{ flexWrap: "wrap" }}>
        <span className="muted small" style={{ fontWeight: 700 }}>Partir d'un modèle :</span>
        {FIGURE_PRESETS.map((p) => (
          <button key={p.id} type="button" className="btn sm ghost" onClick={() => { if (window.confirm(`Remplacer la figure par « ${p.label} » ?`)) onChange(structuredClone(p.spec)); }}>{p.label}</button>
        ))}
      </div>

      <div className="row" style={{ flexWrap: "wrap" }}>
        <span className="small" style={{ fontWeight: 700 }}>Fenêtre</span>
        {(["x", "y"] as const).map((axis) => (
          <span key={axis} className="row small" style={{ gap: 6 }}>
            <i>{axis}</i> de
            <input className="input" type="number" style={{ width: 72 }} value={raw.view[axis][0]} onChange={(e) => set({ view: { ...raw.view, [axis]: [num(e.target.value, raw.view[axis][0]), raw.view[axis][1]] } })} />
            à
            <input className="input" type="number" style={{ width: 72 }} value={raw.view[axis][1]} onChange={(e) => set({ view: { ...raw.view, [axis]: [raw.view[axis][0], num(e.target.value, raw.view[axis][1])] } })} />
          </span>
        ))}
      </div>

      <div className="stack" style={{ gap: 6 }}>
        <div className="row"><span className="small" style={{ fontWeight: 700 }}>Courbes y = f(x)</span><span className="muted small">ex. a*x^2 + b, 2sin(x), (x-1)(x+2)</span></div>
        {raw.curves.map((c, i) => {
          const r = compileExpr(c.expr, ["x", ...names]);
          return (
            <div key={i} className="stack" style={{ gap: 4 }}>
              <div className="row">
                <span className="mono muted">y =</span>
                <input className="input mono" value={c.expr} maxLength={200} aria-invalid={!r.ok} style={!r.ok ? { borderColor: "var(--bad)" } : undefined} onChange={(e) => setCurve(i, { expr: e.target.value })} />
                <select className="select" style={{ width: 160 }} value={c.color} onChange={(e) => setCurve(i, { color: e.target.value as Curve["color"] })}>
                  {FIGURE_COLORS.map((k) => <option key={k} value={k}>{COLOR_LABEL[k]}</option>)}
                </select>
                <select className="select" style={{ width: 120 }} value={c.style} onChange={(e) => setCurve(i, { style: e.target.value as Curve["style"] })}>
                  <option value="solid">Continue</option><option value="dashed">Pointillés</option>
                </select>
                <button type="button" className="icon-btn" aria-label="Retirer la courbe" disabled={raw.curves.length <= 1} onClick={() => set({ curves: raw.curves.filter((_, k) => k !== i), point: raw.point && raw.point.curve >= raw.curves.length - 1 ? { ...raw.point, curve: 0 } : raw.point })}>✕</button>
              </div>
              {!r.ok && <span className="small" style={{ color: "var(--bad)", fontWeight: 600, paddingLeft: 32 }}>{r.error}</span>}
            </div>
          );
        })}
        <div><button type="button" className="btn sm ghost" disabled={raw.curves.length >= 4} onClick={() => set({ curves: [...raw.curves, { expr: "x", color: "amber", style: "solid" }] })}>+ Courbe</button></div>
      </div>

      <div className="stack" style={{ gap: 6 }}>
        <span className="small" style={{ fontWeight: 700 }}>Curseurs (l'élève les fait varier)</span>
        {raw.sliders.length > 0 && (
          <table className="t">
            <thead><tr><th>Nom</th><th>Libellé ({lang})</th><th>Min</th><th>Max</th><th>Pas</th><th>Départ</th><th /></tr></thead>
            <tbody>
              {raw.sliders.map((s, i) => (
                <tr key={i}>
                  <td><input className="input mono" style={{ width: 64 }} value={s.name} maxLength={8} onChange={(e) => setSlider(i, { name: e.target.value.trim() })} /></td>
                  <td><input className="input" value={s.label[lang] ?? ""} placeholder={lang !== "fr" ? s.label.fr : "Coefficient a"} maxLength={60} onChange={(e) => setSlider(i, { label: { ...s.label, [lang]: e.target.value } })} /></td>
                  <td><input className="input" type="number" style={{ width: 70 }} value={s.min} onChange={(e) => setSlider(i, { min: num(e.target.value, s.min) })} /></td>
                  <td><input className="input" type="number" style={{ width: 70 }} value={s.max} onChange={(e) => setSlider(i, { max: num(e.target.value, s.max) })} /></td>
                  <td><input className="input" type="number" style={{ width: 70 }} step={0.1} min={0.01} value={s.step} onChange={(e) => setSlider(i, { step: num(e.target.value, s.step) })} /></td>
                  <td><input className="input" type="number" style={{ width: 70 }} step={s.step} value={s.initial} onChange={(e) => setSlider(i, { initial: num(e.target.value, s.initial) })} /></td>
                  <td><button type="button" className="icon-btn" aria-label="Retirer le curseur" onClick={() => set({ sliders: raw.sliders.filter((_, k) => k !== i) })}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div><button type="button" className="btn sm ghost" disabled={raw.sliders.length >= 4} onClick={() => {
          const free = ["a", "b", "c", "k", "m", "p"].find((n) => !raw.sliders.some((s) => s.name === n)) ?? `p${raw.sliders.length + 1}`;
          set({ sliders: [...raw.sliders, { name: free, label: {}, min: -5, max: 5, step: 0.1, initial: 1 }] });
        }}>+ Curseur</button></div>
      </div>

      <div className="row" style={{ flexWrap: "wrap" }}>
        <label className="row small" style={{ fontWeight: 600 }}>
          <input type="checkbox" checked={!!raw.point} onChange={(e) => set({ point: e.target.checked ? { curve: 0, initialX: 1 } : null })} /> Point mobile sur une courbe
        </label>
        {raw.point && (
          <>
            <select className="select" style={{ width: 140 }} value={raw.point.curve} onChange={(e) => set({ point: { ...raw.point!, curve: Number(e.target.value) } })}>
              {raw.curves.map((c, i) => <option key={i} value={i}>Courbe {i + 1}</option>)}
            </select>
            <span className="row small">départ <i>x</i> = <input className="input" type="number" style={{ width: 72 }} value={raw.point.initialX} onChange={(e) => set({ point: { ...raw.point!, initialX: num(e.target.value, raw.point!.initialX) } })} /></span>
          </>
        )}
      </div>

      {!check.success && <div className="alert err small">{check.error.issues[0]?.message ?? "Figure invalide"}</div>}
    </div>
  );
}
